class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onKeyUp = (evt) => evt.code === 'Escape' && this.close();
    this.onOverlayClick = this.close.bind(this);

    this.addEventListener('keyup', this.onKeyUp);

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.onOverlayClick);

    this.setHeaderCartIconAccessibility();
    this.initRecommendations();
    this.initQuickAdd();
    this.bindGlobalEvents();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    if (!cartLink.hasAttribute('aria-expanded')) {
      cartLink.setAttribute('aria-expanded', 'false');
    }

    cartLink.removeEventListener('click', this._cartClickHandler);
    cartLink.removeEventListener('keydown', this._cartKeyHandler);

    this._cartClickHandler = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };

    this._cartKeyHandler = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    };

    cartLink.addEventListener('click', this._cartClickHandler);
    cartLink.addEventListener('keydown', this._cartKeyHandler);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (cartLink) cartLink.setAttribute('aria-expanded', 'true');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.addEventListener('click', this.onBodyClick);
    this.refreshRecommendationsUI();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('click', this.onBodyClick);

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-expanded', 'false');

    this.closeQuickAdd();
  }

  handleBodyClick(event) {
    const mainButton = event.target.closest('.cart-drawer-recommendation-main');
    if (mainButton) {
      const url = mainButton.getAttribute('data-product-url');
      if (url) window.location.href = url;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      if (!parsedState.sections[section.id]) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.removeEventListener('click', this.onOverlayClick);
        overlay.addEventListener('click', this.onOverlayClick);
      }

      this.setHeaderCartIconAccessibility();
      this.initRecommendations();
      this.initQuickAdd();
      this.open();
      this.refreshRecommendationsUI();
      this.convertDrawerPrices();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  bindGlobalEvents() {
    if (this._boundGlobalEvents) return;
    this._boundGlobalEvents = true;

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.quickAddModal && !this.quickAddModal.hidden) {
        this.closeQuickAdd();
      }
    });

    document.addEventListener('click', async (event) => {
      const cta = event.target.closest('.cart-drawer-recommendation-cta');
      if (!cta || !this.contains(cta)) return;

      event.preventDefault();

      const directVariantId = cta.getAttribute('data-direct-variant-id');
      const handle = cta.getAttribute('data-product-handle');

      if (directVariantId) {
        await this.handleDirectAdd(cta, directVariantId);
        return;
      }

      if (handle) {
        await this.openQuickAddByHandle(handle, cta);
      }
    });
  }

  initRecommendations() {
    const recoLists = this.querySelectorAll('[data-reco-list]');
    if (!recoLists.length) return;

    if (!this._recoResizeHandler) {
      this._recoResizeHandler = this.debounce(() => this.refreshRecommendationsUI(), 120);
      window.addEventListener('resize', this._recoResizeHandler);
    }

    recoLists.forEach((list) => {
      const nav = this.findNavForList(list);
      if (!nav) return;

      if (!list.dataset.recoBound) {
        list.addEventListener(
          'scroll',
          () => {
            if (!window.matchMedia('(max-width: 749px)').matches) return;
            window.requestAnimationFrame(() => this.updateDots(nav, list));
          },
          { passive: true }
        );

        const prev = nav.querySelector('[data-reco-prev]');
        const next = nav.querySelector('[data-reco-next]');

        prev?.addEventListener('click', () => {
          list.scrollBy({ left: -list.clientWidth * 0.9, behavior: 'smooth' });
        });

        next?.addEventListener('click', () => {
          list.scrollBy({ left: list.clientWidth * 0.9, behavior: 'smooth' });
        });

        list.dataset.recoBound = 'true';
      }

      this.rebuildDots(nav, list);
      this.updateDots(nav, list);
      this.toggleNavVisibility(nav, list);
      this.normalizeTitles(list);
    });
  }

  refreshRecommendationsUI() {
    const recoLists = this.querySelectorAll('[data-reco-list]');
    recoLists.forEach((list) => {
      const nav = this.findNavForList(list);
      if (!nav) return;
      this.rebuildDots(nav, list);
      this.updateDots(nav, list);
      this.toggleNavVisibility(nav, list);
      this.normalizeTitles(list);
    });
  }

  findNavForList(list) {
    const root = list.closest('.cart-drawer-recommendations-column');
    return root ? root.querySelector('[data-reco-nav]') : null;
  }

  rebuildDots(nav, list) {
    const dotsWrap = nav.querySelector('[data-reco-dots]');
    if (!dotsWrap) return;

    const slides = list.querySelectorAll('.reco-slide');
    const fragment = document.createDocumentFragment();

    slides.forEach((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'reco-dot';
      button.setAttribute('aria-label', `Ir para item ${index + 1}`);
      button.addEventListener('click', () => {
        list.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
      fragment.appendChild(button);
    });

    dotsWrap.replaceChildren(fragment);
  }

  updateDots(nav, list) {
    const dots = nav.querySelectorAll('.reco-dot');
    const slides = list.querySelectorAll('.reco-slide');
    if (!dots.length || !slides.length) return;

    const center = list.scrollLeft + list.clientWidth / 2;
    let activeIndex = 0;
    let bestDistance = Infinity;

    slides.forEach((slide, index) => {
      const midpoint = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(midpoint - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        activeIndex = index;
      }
    });

    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  }

  toggleNavVisibility(nav, list) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const hasSlides = list.querySelectorAll('.reco-slide').length > 1;
    nav.style.display = isMobile && hasSlides ? 'flex' : 'none';
  }

  normalizeTitles(list) {
    const titles = list.querySelectorAll('.cart-drawer-recommendation-title');
    if (!titles.length) return;

    titles.forEach((title) => {
      title.style.minHeight = '';
    });

    let maxHeight = 0;
    titles.forEach((title) => {
      maxHeight = Math.max(maxHeight, title.offsetHeight);
    });

    list.style.setProperty('--reco-title-height', `${maxHeight}px`);

    titles.forEach((title) => {
      title.style.minHeight = 'var(--reco-title-height)';
    });
  }

  initQuickAdd() {
    this.quickAddModal = this.querySelector('[data-reco-qa]');
    if (!this.quickAddModal) return;

    this.qaThumb = this.quickAddModal.querySelector('[data-qa-thumb]');
    this.qaTitle = this.quickAddModal.querySelector('[data-qa-title]');
    this.qaForm = this.quickAddModal.querySelector('[data-qa-form]');
    this.qaOptions = this.quickAddModal.querySelector('[data-qa-options]');
    this.qaStatus = this.quickAddModal.querySelector('[data-qa-status]');
    this.qaSubmit = this.quickAddModal.querySelector('[data-qa-submit]');

    if (!this.quickAddModal.dataset.bound) {
      this.quickAddModal.querySelectorAll('[data-reco-qa-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeQuickAdd());
      });

      this.qaForm?.addEventListener('submit', async (event) => {
        event.preventDefault();
        await this.submitQuickAdd();
      });

      this.quickAddModal.dataset.bound = 'true';
    }

    this.ensureQuickAddTitleScaffold();
  }

  ensureQuickAddTitleScaffold() {
    if (!this.qaTitle) return;

    let titleText = this.qaTitle.querySelector('.qa-title-text');
    let priceLine = this.qaTitle.querySelector('.qa-price-line');

    if (!titleText) {
      titleText = document.createElement('span');
      titleText.className = 'qa-title-text';
      this.qaTitle.appendChild(titleText);
    }

    if (!priceLine) {
      priceLine = document.createElement('div');
      priceLine.className = 'qa-price-line';
      this.qaTitle.appendChild(priceLine);
    }

    this.qaTitleText = titleText;
    this.qaPriceLine = priceLine;
  }

  async handleDirectAdd(button, variantId) {
    const originalText = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adicionando...';

    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });

      if (!response.ok) {
        button.textContent = 'Tente novamente';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        return;
      }

      button.textContent = 'Adicionado';
      await this.refreshCartDrawer();
    } catch (error) {
      console.error('Erro ao adicionar item recomendado:', error);
      button.textContent = 'Tente novamente';
      button.disabled = false;
      button.removeAttribute('aria-busy');
      return;
    }

    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }, 900);
  }

  async openQuickAddByHandle(handle, trigger) {
    if (!this.quickAddModal) return;

    const card = trigger.closest('.reco-slide, .cart-drawer-recommendation');
    const fallbackImage = card?.querySelector('img')?.currentSrc || card?.querySelector('img')?.src || '';
    const fallbackTitle =
      card?.querySelector('.cart-drawer-recommendation-title')?.textContent?.trim() || '';

    this.ensureQuickAddTitleScaffold();

    if (this.qaThumb) {
      this.qaThumb.innerHTML = fallbackImage ? `<img src="${fallbackImage}" alt="">` : '';
    }

    if (this.qaTitleText) this.qaTitleText.textContent = fallbackTitle;
    if (this.qaPriceLine) this.qaPriceLine.innerHTML = '';
    if (this.qaStatus) this.qaStatus.textContent = '';
    if (this.qaOptions) this.qaOptions.innerHTML = '';
    if (this.qaSubmit) this.qaSubmit.disabled = true;

    try {
      const response = await fetch(`/products/${handle}.js`, {
        credentials: 'same-origin',
      });

      this.quickAddProduct = await response.json();
      this.buildQuickAddOptions(this.quickAddProduct);
      this.pickFirstAvailableVariant();
      this.openQuickAdd();
    } catch (error) {
      console.error('Erro ao abrir quick add:', error);
    }
  }

  openQuickAdd() {
    if (!this.quickAddModal) return;
    this.quickAddModal.hidden = false;
    this.quickAddModal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }

  closeQuickAdd() {
    if (!this.quickAddModal) return;
    this.quickAddModal.hidden = true;
    this.quickAddModal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';

    if (this.qaOptions) this.qaOptions.innerHTML = '';
    if (this.qaStatus) this.qaStatus.textContent = '';
    if (this.qaThumb) this.qaThumb.innerHTML = '';
    if (this.qaTitleText) this.qaTitleText.textContent = '';
    if (this.qaPriceLine) this.qaPriceLine.innerHTML = '';
    if (this.qaSubmit) this.qaSubmit.disabled = true;

    this.quickAddProduct = null;
    this.selectedOptions = null;
    this.activeVariant = null;
  }

  buildQuickAddOptions(product) {
    if (!this.qaOptions || !product) return;

    this.qaOptions.innerHTML = '';
    this.selectedOptions = {};

    const optionNames = (product.options || []).map((option) =>
      typeof option === 'string' ? option : option?.name
    ).filter(Boolean);

    optionNames.forEach((optionName, index) => {
      const wrapper = document.createElement('div');
      const selectId = `qa-opt-${index}`;

      wrapper.innerHTML = `
        <label for="${selectId}">${this.escapeHtml(optionName)}</label>
        <select id="${selectId}" data-opt-index="${index}" aria-label="${this.escapeHtml(optionName)}"></select>
      `;

      const select = wrapper.querySelector('select');
      select.addEventListener('change', () => this.recomputeVariantFrom(index));

      this.qaOptions.appendChild(wrapper);
    });

    this.recomputeVariantFrom(0);
  }

  pickFirstAvailableVariant() {
    if (!this.quickAddProduct?.variants?.length) return;

    const firstAvailable =
      this.quickAddProduct.variants.find((variant) => variant.available) ||
      this.quickAddProduct.variants[0];

    const selects = this.qaOptions?.querySelectorAll('select[data-opt-index]');
    if (!selects?.length) return;

    selects.forEach((select, index) => {
      const value = firstAvailable[`option${index + 1}`];
      if (!value) return;

      const optionExists = Array.from(select.options).find((option) => option.value === String(value));
      if (optionExists) select.value = String(value);
    });

    this.recomputeVariantFrom(0);
  }

  recomputeVariantFrom() {
    const product = this.quickAddProduct;
    if (!product?.variants?.length || !this.qaOptions) return;

    const optionNames = (product.options || []).map((option) =>
      typeof option === 'string' ? option : option?.name
    ).filter(Boolean);

    const selects = Array.from(this.qaOptions.querySelectorAll('select[data-opt-index]')).sort(
      (a, b) => Number(a.dataset.optIndex) - Number(b.dataset.optIndex)
    );

    const currentSelection = {};

    for (let index = 0; index < optionNames.length; index += 1) {
      const optionName = optionNames[index];
      const allowedValues = new Set();

      product.variants.forEach((variant) => {
        let matchesPrevious = true;

        for (let prevIndex = 0; prevIndex < index; prevIndex += 1) {
          const prevName = optionNames[prevIndex];
          const prevValue = currentSelection[prevName];
          if (prevValue && String(variant[`option${prevIndex + 1}`]) !== String(prevValue)) {
            matchesPrevious = false;
            break;
          }
        }

        if (matchesPrevious) allowedValues.add(String(variant[`option${index + 1}`]));
      });

      const select = selects[index];
      const previousValue = select.value;
      const values = Array.from(allowedValues);

      select.innerHTML = values
        .map((value) => `<option value="${this.escapeHtml(value)}">${this.escapeHtml(value)}</option>`)
        .join('');

      select.value = values.includes(previousValue) ? previousValue : values[0];
      currentSelection[optionName] = select.value;
    }

    this.selectedOptions = currentSelection;

    this.activeVariant =
      product.variants.find((variant) =>
        optionNames.every((name, index) => String(variant[`option${index + 1}`]) === String(currentSelection[name]))
      ) || null;

    if (!this.activeVariant) {
      if (this.qaPriceLine) this.qaPriceLine.innerHTML = '';
      if (this.qaStatus) this.qaStatus.textContent = 'Esta combinação não existe.';
      if (this.qaSubmit) this.qaSubmit.disabled = true;
      return;
    }

    if (this.qaTitleText) this.qaTitleText.textContent = product.title || '';
    this.updateQuickAddPrice(this.activeVariant);
    this.updateQuickAddImage(this.activeVariant, product);

    if (!this.activeVariant.available) {
      if (this.qaStatus) this.qaStatus.textContent = 'Indisponível no momento.';
      if (this.qaSubmit) this.qaSubmit.disabled = true;
    } else {
      if (this.qaStatus) this.qaStatus.textContent = '';
      if (this.qaSubmit) this.qaSubmit.disabled = false;
    }
  }

  updateQuickAddPrice(variant) {
    if (!this.qaPriceLine || !variant) return;

    const currentCents = Number(variant.price || 0);
    const compareCents = Number(variant.compare_at_price || 0);

    const priceHtml = `<span class="qa-price-current" style="font-weight:700">${this.formatMoney(currentCents)}</span>`;
    const compareHtml =
      compareCents > currentCents
        ? `<span class="qa-price-compare" style="font-size:.92em;text-decoration:line-through;opacity:.7;margin-left:.4rem">${this.formatMoney(compareCents)}</span>`
        : '';

    this.qaPriceLine.innerHTML = `${priceHtml}${compareHtml}`;
  }

  updateQuickAddImage(variant, product) {
    if (!this.qaThumb) return;

    const variantImage = variant?.featured_image?.src || variant?.featured_image?.url;
    const productImage = product?.images?.[0];
    const rawSrc = variantImage || productImage || '';

    if (!rawSrc) return;

    const src = this.normalizeShopifyImageUrl(rawSrc, 320);
    this.qaThumb.innerHTML = `<img src="${src}" alt="${this.escapeHtml(product?.title || '')}">`;
  }

  async submitQuickAdd() {
    if (!this.activeVariant || !this.qaSubmit) return;

    const originalLabel = this.qaSubmit.textContent;
    this.qaSubmit.disabled = true;
    this.qaSubmit.textContent = 'Adicionando...';
    if (this.qaStatus) this.qaStatus.textContent = '';

    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: Number(this.activeVariant.id), quantity: 1 }],
        }),
      });

      if (!response.ok) {
        let payload = {};
        try {
          payload = await response.json();
        } catch (e) {
          payload = {};
        }

        if (this.qaStatus) {
          this.qaStatus.textContent =
            payload?.description || payload?.message || 'Não foi possível adicionar. Tente novamente.';
        }

        this.qaSubmit.disabled = false;
        this.qaSubmit.textContent = originalLabel;
        return;
      }

      this.qaSubmit.textContent = 'Adicionado';
      await this.refreshCartDrawer();
      setTimeout(() => this.closeQuickAdd(), 150);
    } catch (error) {
      console.error('Erro no quick add:', error);
      if (this.qaStatus) this.qaStatus.textContent = 'Erro de rede. Tente novamente.';
      this.qaSubmit.disabled = false;
      this.qaSubmit.textContent = originalLabel;
    }
  }

  async refreshCartDrawer() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`, {
        credentials: 'same-origin',
      });

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      const newCartDrawer = parsed.querySelector('#CartDrawer');
      const currentCartDrawer = this.querySelector('#CartDrawer');

      if (newCartDrawer && currentCartDrawer) {
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }

      const newBubble = parsed.querySelector('#shopify-section-cart-icon-bubble, #cart-icon-bubble') ||
        parsed.getElementById('cart-icon-bubble');
      const currentBubbleSection = document.querySelector('#shopify-section-cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');

      if (currentBubbleSection && newBubble?.closest?.('.shopify-section')) {
        currentBubbleSection.innerHTML = newBubble.closest('.shopify-section').innerHTML;
      } else if (currentBubble && newBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }

      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.removeEventListener('click', this.onOverlayClick);
        overlay.addEventListener('click', this.onOverlayClick);
      }

      this.classList.remove('is-empty');
      this.setHeaderCartIconAccessibility();
      this.initRecommendations();
      this.initQuickAdd();
      this.convertDrawerPrices();
      this.refreshRecommendationsUI();
      this.open();

      ['cart:refresh', 'cart:updated', 'cart:change', 'theme:cart:refresh'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
        window.dispatchEvent(new CustomEvent(name));
      });
    } catch (error) {
      console.warn('Fallback para reload do drawer:', error);
      window.location.reload();
    }
  }

  convertDrawerPrices(root = this) {
    const targets = root.querySelectorAll('.cart-drawer-recommendations-column .money[data-cents]');
    if (!targets.length) return;

    targets.forEach((element) => {
      const cents = Number(element.getAttribute('data-cents') || 0);
      element.textContent = this.formatMoney(cents);
    });
  }

  formatMoney(cents) {
    try {
      if (window.Shopify?.formatMoney) {
        const format =
          window?.Shopify?.currency?.active_format ||
          window?.theme?.moneyFormat ||
          window?.Shopify?.money_format ||
          '${{amount}}';
        return window.Shopify.formatMoney(cents, format);
      }
    } catch (error) {
      console.warn('formatMoney fallback:', error);
    }

    const currency =
      window?.Shopify?.currency?.active ||
      document.documentElement.getAttribute('data-currency') ||
      'USD';

    return (Number(cents || 0) / 100).toLocaleString(undefined, {
      style: 'currency',
      currency,
    });
  }

  normalizeShopifyImageUrl(url, width) {
    try {
      let normalized = url;
      if (normalized.startsWith('//')) normalized = `${window.location.protocol}${normalized}`;
      if (!normalized.startsWith('http')) normalized = new URL(normalized, window.location.origin).toString();

      const parsed = new URL(normalized);
      if (!parsed.searchParams.has('width')) parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (match) => {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };
      return map[match];
    });
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);